import { ConversionAttribution } from '@/common/model/recommend/conversion'
import * as z from 'zod'

import {
  CreateDistributionVars,
  DistributionVariantInput,
  UpdateDistributionVars
} from '@/common/api/params/recommendationDistribution'
import { NameLabelMapping } from '@/common/app/message'
import { dateToRequest } from '@/common/form/date'
import { NumberValue, StringLength, validateCode } from '@/common/form/schema'
import {
  ABCampaignGoalMetric,
  ABCampaignSegments,
  ABCampaignTargetDevice
} from '@/common/model/recommend/abCampaign'
import {
  RecommendationDistribution,
  RecommendationDistributionStatus
} from '@/common/model/recommend/recommendationDistribution'

export const AB_VARIANT_MIN = 2
export const AB_VARIANT_MAX = 5

const variantSchema = z.object({
  widgetId: z.string(), // セレクタ値（''=未選択）
  abTraffic: z.number().int().min(0).max(100)
})

export const distributionSchema = z
  .object({
    id: z.number().readonly().optional(),
    siteId: z.number().readonly(),
    name: z.string().min(1).max(StringLength.NORMAL_MAX),
    code: validateCode().min(1),
    status: z.custom<RecommendationDistributionStatus>(),
    abEnabled: z.boolean(),
    variantCount: z.number().int().min(1).max(AB_VARIANT_MAX),
    goalMetric: z.custom<ABCampaignGoalMetric>(),
    // KPI目標がコンバージョン率のときだけ意味を持つ。A/Bの勝者判定に使う種別
    cvAttribution: z.custom<ConversionAttribution>(),
    targetDevices: z.custom<ABCampaignTargetDevice>().array(),
    targetSegment: z.custom<ABCampaignSegments>(),
    minSamplePerVariant: z.number().int().positive().max(NumberValue.STOCK_MAX),
    confidenceLevel: z.number().positive().max(1),
    distributesAt: z.date().nullable(),
    variants: variantSchema.array()
  })
  .superRefine((data, ctx) => {
    const count = data.abEnabled ? data.variantCount : 1
    const active = data.variants.slice(0, count)

    // 表示パーツが全て選択されていること
    active.forEach((v, index) => {
      if (!v.widgetId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '表示パーツを選択してください',
          path: ['variants', index, 'widgetId']
        })
      }
    })

    // 同一パーツの重複禁止
    const ids = active.map((v) => v.widgetId).filter(Boolean)
    if (new Set(ids).size !== ids.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '同一の表示パーツを複数指定できません',
        path: ['variants']
      })
    }

    // A/Bテスト時はトラフィック配分の合計が100
    if (data.abEnabled) {
      const total = active.reduce((sum, v) => sum + (v.abTraffic || 0), 0)
      if (total !== 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `トラフィック配分の合計は100にしてください（現在 ${total}）`,
          path: ['variants']
        })
      }
    }
  })

export type DistributionFormSchemaType = z.infer<typeof distributionSchema>

const makeEmptyVariants = (): DistributionFormSchemaType['variants'] =>
  Array.from({ length: AB_VARIANT_MAX }, () => ({ widgetId: '', abTraffic: 0 }))

export const convertDistributionSchemaToVars = (
  schema: DistributionFormSchemaType
): CreateDistributionVars | UpdateDistributionVars => {
  const count = schema.abEnabled ? schema.variantCount : 1
  const variants: DistributionVariantInput[] = schema.variants.slice(0, count).map((v) => ({
    widget_id: Number(v.widgetId),
    ab_traffic: schema.abEnabled ? v.abTraffic : 100
  }))

  const base = {
    name: schema.name,
    code: schema.code,
    status: schema.status,
    ab_enabled: schema.abEnabled,
    goal_metric: schema.goalMetric,
    cv_attribution: schema.cvAttribution,
    target_devices: schema.targetDevices,
    target_segments: [schema.targetSegment],
    min_sample_per_variant: schema.minSamplePerVariant,
    confidence_level: schema.confidenceLevel,
    distributes_at: schema.distributesAt ? dateToRequest(schema.distributesAt) : null,
    variants
  }

  if (schema.id !== undefined) {
    return { site_id: schema.siteId, distribution_id: schema.id, ...base }
  }
  return { site_id: schema.siteId, ...base }
}

export const convertDistributionToSchema = (
  distribution: RecommendationDistribution
): DistributionFormSchemaType => {
  const variants = makeEmptyVariants()
  distribution.variants.forEach((v, index) => {
    if (index < AB_VARIANT_MAX) {
      variants[index] = { widgetId: String(v.widget_id), abTraffic: v.ab_traffic }
    }
  })

  const count = distribution.ab_enabled
    ? Math.min(AB_VARIANT_MAX, Math.max(AB_VARIANT_MIN, distribution.variants.length))
    : 1

  return {
    id: distribution.id,
    siteId: distribution.site_id,
    name: distribution.name,
    code: distribution.code,
    status: distribution.status,
    abEnabled: distribution.ab_enabled,
    variantCount: count,
    goalMetric: distribution.goal_metric,
    cvAttribution: distribution.cv_attribution ?? ConversionAttribution.ASSIST,
    targetDevices: distribution.target_devices ?? [],
    targetSegment: distribution.target_segments?.[0] ?? ABCampaignSegments.ALL,
    minSamplePerVariant: distribution.min_sample_per_variant,
    confidenceLevel: distribution.confidence_level,
    distributesAt: distribution.distributes_at ? new Date(distribution.distributes_at) : null,
    variants
  }
}

export const makeDefaultDistributionSchema = (siteId: number): DistributionFormSchemaType => ({
  siteId,
  name: '',
  code: '',
  status: RecommendationDistributionStatus.DRAFT,
  abEnabled: false,
  variantCount: AB_VARIANT_MIN,
  goalMetric: ABCampaignGoalMetric.CTR,
  cvAttribution: ConversionAttribution.ASSIST,
  targetDevices: [
    ABCampaignTargetDevice.PC,
    ABCampaignTargetDevice.MOBILE,
    ABCampaignTargetDevice.TABLET
  ],
  targetSegment: ABCampaignSegments.ALL,
  minSamplePerVariant: 100,
  confidenceLevel: 0.95,
  distributesAt: null,
  variants: makeEmptyVariants()
})

export const distributionNameLabelMapping: NameLabelMapping = {
  name: '配信管理名',
  code: '埋め込みコード',
  status: 'ステータス',
  variants: '表示パーツ',
  minSamplePerVariant: '最小サンプル数',
  distributesAt: '配信開始'
}
